import type { CaptureResult } from 'posthog-js';

// Registrable domains of the third-party marketing scripts the storefront loads
// through CustomPixels. `capture_exceptions` reports every unhandled rejection,
// so a vendor outage (for example Klaviyo's onsite atlas bundle returning 500s)
// becomes a first-party issue. We match on the script host, not the issue,
// because minified vendor bundles shift stack columns between builds and split
// one throw site into many column-level fingerprints.
export const THIRD_PARTY_SCRIPT_DOMAINS = [
	'klaviyo.com',
	'vibe.co',
	'snapchat.com',
	'sc-static.net',
	'tiktok.com',
	'facebook.net',
	'googletagmanager.com',
	'doubleclick.net',
	'googleadservices.com',
	'postscript.io',
	'triplewhale.com',
];

function isThirdPartyScriptHost(hostname: string): boolean {
	return THIRD_PARTY_SCRIPT_DOMAINS.some(
		(domain) => hostname === domain || hostname.endsWith(`.${domain}`),
	);
}

// The Mark pixel wraps `window.fetch` and returns the original promise, so its
// frame appears in the stack of every vendor fetch that fails. It is a
// pass-through frame, not the throw site, so it must not count as first-party.
const MARK_PIXEL_HOSTNAME = 'mark.workmanusa.com';
const MARK_PIXEL_PATHNAME = '/api/pixel/mark.js';

function frameUrl(filename: unknown): URL | null {
	if (typeof filename !== 'string') return null;
	try {
		return new URL(filename);
	} catch {
		return null;
	}
}

function isFirstPartyFrame(url: URL): boolean {
	if (isThirdPartyScriptHost(url.hostname)) return false;
	if (url.hostname === MARK_PIXEL_HOSTNAME && url.pathname === MARK_PIXEL_PATHNAME) return false;
	return true;
}

// Drops an exception event unless it keeps at least one first-party frame.
// A first-party frame has a resolvable URL that is not a vendor script host and
// not the Mark pixel script. This removes two noise classes:
//   - vendor-only throws, where every resolvable frame is a vendor script or the
//     Mark pixel's fetch wrapper;
//   - frameless throws, where no frame has a resolvable URL at all. A
//     cross-origin script reports these with a type, a value, and a mechanism
//     but no stack, so each new minified value ("s.id", "o.id", "Script
//     error.") mints a fake first-party issue nobody can act on.
export function dropThirdPartyScriptExceptions(event: CaptureResult | null): CaptureResult | null {
	if (event?.event !== '$exception') return event;

	const exceptions = event.properties?.$exception_list;
	if (!Array.isArray(exceptions)) return event;

	for (const exception of exceptions) {
		const frames = exception?.stacktrace?.frames;
		if (!Array.isArray(frames)) continue;

		for (const frame of frames) {
			const url = frameUrl(frame?.filename);
			if (url && isFirstPartyFrame(url)) return event;
		}
	}

	return null;
}
